<template>
  <div>
    <AiChatWidgetChatBubble
      v-if="aiChatEnabled"
      :key="`bubble-${currentLanguage}-${languageChangeTrigger}`"
      :active-state="bubbleState"
      :client-info="clientInfo"
      @state-change="handleStateChange"
      @first-hover="handleFirstHover"
    />
    <AiChatWidgetSmartDrawer
      :key="`drawer-${currentLanguage}-${languageChangeTrigger}`"
      :is-open="isOpen"
      :active-state="bubbleState"
      :should-load="loadDrawer"
      :client-info="clientInfo"
      @state-change="handleStateChange"
      @close="handleDrawerClose"
      @language-selected="handleLanguageSelected"
    />
  </div>
</template>

<script setup>
import { useStore } from 'vuex';
import { getStreamConversation, deleteSession } from '~/helpers/api/stream';
import { setGlobalChatInstance, useAiChatWidget } from '~/composables/useAiChatWidget';

const props = defineProps({
  clientInfo: {
    type: Object,
    required: true,
  },
});

let chat = null;
let isConnecting = false;
let eventListenerAdded = false;

const store = useStore();
const secondaryOrgId = store.state.secondaryOrgId;
const writableApi = useRuntimeConfig().public.WRITABLE_BASE_API_URL;
const { sessionId: sId } = useSession();

const languageChangeTrigger = ref(0);

const { bubbleState, aiChatEnabled, isOpen, loadDrawer, handleStateChange, handleDrawerClose, handleFirstHover } =
  useAiChatWidget();
const { $intl, $ld } = useNuxtApp();
const currentLanguage = computed(() => $intl.locale);

const handlePageUnload = () => {
  if (chat && chat.isConnected.value && sId.value) {
    void deleteSession({
      endpoint: writableApi,
      secondaryOrgId,
      sessionId: sId.value,
    });
  }
};

const handleLanguageSelected = async (code) => {
  const language = props.clientInfo.languages.find((language) => language.code === code);
  if (language?.code !== $intl.locale) {
    $intl.$setLocale(language?.code);
    languageChangeTrigger.value++;
  }
};

const canInitializeStreamConnection = computed(() => {
  return import.meta.client && $ld?.flags?.cmsAiChat && !isConnecting;
});

const connectToStream = async () => {
  if (!canInitializeStreamConnection.value) return;

  if (!chat) {
    chat = useChat();
    setGlobalChatInstance(chat);
  }

  if (!chat.isConnected.value) {
    isConnecting = true;
    try {
      const config = {
        endpoint: writableApi,
        secondaryOrgId,
        sessionId: sId.value,
      };

      const welcomeMessage = props.clientInfo?.chatConfiguration?.branding?.welcome_message;
      await chat.connectWithProvider(async () => await getStreamConversation(config), config, { welcomeMessage });
    } catch (e) {
      console.error("Can't connect to Stream", e);
    } finally {
      isConnecting = false;
    }
  }
};

const chatWidgetReady = computed(() => {
  return import.meta.client || $ld?.flags?.cmsAiChat;
});

watch(bubbleState, (newState) => {
  if (!chatWidgetReady.value) return;

  if (newState === 'closed') {
    if (eventListenerAdded) {
      window.removeEventListener('pagehide', handlePageUnload);
      eventListenerAdded = false;
    }
  } else {
    if (!eventListenerAdded) {
      window.addEventListener('pagehide', handlePageUnload);
      eventListenerAdded = true;
    }
    connectToStream();
  }
});

onUnmounted(() => {
  window.removeEventListener('pagehide', handlePageUnload);
});
</script>
